import React, { useEffect, useState } from "react";
import { Gift, X } from "lucide-react";
import { promoConfig, isPromoActive, getPromoTimeLeft } from "../data/promoConfig";

const STORAGE_KEY = "lm_promo_dismissed";

const pad = (n) => String(n).padStart(2, "0");

export const PromoBanner = ({ onActiveChange }) => {
  const [active, setActive] = useState(false);
  const [tick, setTick] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Initial state
  useEffect(() => {
    setActive(isPromoActive());
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
  }, []);

  // Tick every second
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      const stillActive = isPromoActive();
      if (!stillActive) {
        setActive(false);
        return;
      }
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  // Notify parent (Header/Hero) about visibility for layout offset
  useEffect(() => {
    onActiveChange?.(active && !dismissed);
    document.documentElement.style.setProperty(
      "--promo-banner-h",
      active && !dismissed ? "var(--promo-h, 56px)" : "0px"
    );
  }, [active, dismissed, onActiveChange]);

  const handleClaim = (e) => {
    e.preventDefault();
    sessionStorage.setItem("lm_promo_intent", promoConfig.code);
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Notify contact form to autofill
    window.dispatchEvent(new CustomEvent("lm:promo-prefill", { detail: { code: promoConfig.code } }));
  };

  const handleDismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  if (!active || dismissed) return null;

  const { days, hours, minutes, seconds } = getPromoTimeLeft();

  return (
    <div
      data-testid="promo-banner"
      role="region"
      aria-label="Offre spéciale Pentecôte"
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#C47A66] via-[#C9A869] to-[#C47A66] text-[#0E1A2B] shadow-[0_4px_20px_rgba(0,0,0,0.35)]"
      style={{ "--promo-h": "56px" }}
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 md:px-10 lg:px-14 h-14 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Gift size={18} className="shrink-0" />
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 min-w-0">
            <p className="text-[11px] sm:text-[13px] font-semibold tracking-wider uppercase leading-tight whitespace-nowrap">
              {promoConfig.title}
            </p>
            <p className="text-[11px] sm:text-sm font-light leading-tight truncate">
              <span className="hidden sm:inline">{promoConfig.description}&nbsp;</span>
              <span className="font-mono font-bold tracking-[0.18em] bg-[#0E1A2B] text-[#C9A869] px-2 py-0.5 ml-0.5">
                {promoConfig.code}
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <div
            data-testid="promo-countdown"
            className="hidden xs:flex items-center gap-1 font-mono text-[#0E1A2B] text-sm sm:text-base font-bold tracking-wider"
            aria-live="polite"
          >
            {days > 0 && <span>{days}j</span>}
            <span>{pad(hours)}</span>
            <span className="opacity-50">:</span>
            <span>{pad(minutes)}</span>
            <span className="opacity-50 hidden sm:inline">:</span>
            <span className="hidden sm:inline">{pad(seconds)}</span>
          </div>

          <button
            onClick={handleClaim}
            data-testid="promo-cta"
            className="bg-[#0E1A2B] text-[#C9A869] text-[10px] sm:text-[11px] tracking-[0.18em] uppercase font-semibold px-3 sm:px-5 py-2 hover:bg-[#1A2A40] transition-colors duration-300"
          >
            <span className="hidden sm:inline">{promoConfig.ctaLabel}</span>
            <span className="sm:hidden">Réclamer</span>
          </button>

          <button
            onClick={handleDismiss}
            data-testid="promo-dismiss"
            aria-label="Fermer"
            className="p-1.5 text-[#0E1A2B]/70 hover:text-[#0E1A2B] transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
